import { assign, cloneDeep } from 'lodash-es'
import type { LoginStatus, UserAuthState } from '@/store/modules/auth/type'
import { PermissionRole, UserAccountType, UserViewType } from '@/store/modules/auth/type'
import { getPermissionGroup, getRdxToken, loginTgc, logout as userLogout } from '@/api/auth'
import { clearSimulated } from '@/utils/simulated'
import { clearCsToken, clearCustomerId, clearToken, setToken } from '@/utils/auth'
import { removeSessionStorage, saveCookie } from '@/utils/cache'
import { COOKIE_JESSSION_ID_KEY } from '@/constant/cache-key'
import useRequest from '@/composables/use-request'
import store from '@/store'
import { LOGIN_SUCCESS } from '@/constant/event-name-key'
import { setUserId, trackPageView } from '@/directives/track/utils'
import { clearLoginCallbackUrl, getLoginEntryUrl, isLocalDebugDomain, resolveAuthRedirectUrl } from '@/utils/auth-redirect'

const { get } = useRequest()
const enableLocalDebugUserDefaults = import.meta.env.DEV
const SESSION_RESET_KEYS = ['globalSearch', 'stp_header_view_mode'] as const
// const enableLocalDebugUserDefaults = false

const defaultUser: UserAuthState = {
  name: '',
  avatar: '',
  email: '',
  current_enterprise: '',
  permissionGroup: [],
  refresh_token: '',
  user_name: '',
  nick_name: '',
  tenant_id: '',
  user_id: '',
  dept_id: [],
  role_id: [],
  role_name: [],
  enterprise_id: [],
  account: '',
  access_token: '', // 鉴权的token
  token_type: '',
  expires_in: 0,
  license: '',
  simulated_login_flag: 0,
  simulated_login_ticket: '',
  current_enterprise_name: '',
  current_enterprise_name_cn: '',
  is_admin: false,
  highest_authority_organization_id: '',
  role_permission: PermissionRole.Guest,
  user_account_types: [],
  // 生产默认态保持为空，避免未完成登录回填前就被写死成某个 BU / 视图权限。
  // 仅本地调试环境保留最小兜底，方便联调 Header 和 BU 首页。
  bu_ids: enableLocalDebugUserDefaults ? ['TIM BU'] : [],
  user_view_types: enableLocalDebugUserDefaults ? [UserViewType.Broker, UserViewType.Bu] : [],
}

const useAuthStore = defineStore('auth', () => {
  const user = ref<UserAuthState>(cloneDeep(defaultUser))
  const loginStatus = ref<LoginStatus>(0)
  // 是否是超级管理员
  const superAdmin = computed(() => user.value.user_id === 'SssUser0001')

  // 账号类型缓存：
  // user_account_types 表示“账号身份”，更多用于历史权限判断和业务模块开关，
  // 例如 Broker / Customer / SuperAdmin / BU 等。
  // 它不直接决定首页 Header 上是否允许切换视图。
  const permissionCache = computed(() => {
    const types = user.value.user_account_types
    return {
      types,
      length: types.length,
      hasSuper: types.includes(UserAccountType.SuperAdmin),
      hasBroker: types.includes(UserAccountType.Broker),
      hasCustomer: types.includes(UserAccountType.Customer),
      hasCustomerService: types.includes(UserAccountType.CustomerService),
      hasBu: types.includes(UserAccountType.Bu),
    }
  })
  // 视图类型缓存：
  // user_view_types 表示“当前用户允许查看的数据视角”，
  // 是新版 BU / Broker 首页切换的直接依据。
  // 例如：
  // - ['BROKER_VIEW'] => 只能看 Broker 视角
  // - ['BU_VIEW', 'BROKER_VIEW'] => Header 上允许切换视角
  const viewTypeCache = computed(() => {
    const types = user.value.user_view_types?.length ? user.value.user_view_types : [UserViewType.Broker]
    return {
      types,
      hasBroker: types.includes(UserViewType.Broker),
      hasBu: types.includes(UserViewType.Bu),
    }
  })

  // 包含权限检查
  const includeSuperView = computed(() => permissionCache.value.hasSuper)
  const includeBrokerView = computed(() => permissionCache.value.hasBroker)
  const includeCustomerView = computed(() => permissionCache.value.hasCustomer)
  const includeCustomerServiceView = computed(() => permissionCache.value.hasCustomerService)
  const includeBuAccountType = computed(() => permissionCache.value.hasBu)
  const hasBrokerUserViewType = computed(() => viewTypeCache.value.hasBroker)
  const hasBuUserViewType = computed(() => viewTypeCache.value.hasBu)

  // 单一权限检查 - 优化版本
  const onlySuperView = computed(() => {
    const cache = permissionCache.value
    return cache.length === 1 && cache.types[0] === UserAccountType.SuperAdmin
  })
  const onlyBrokerView = computed(() => {
    const cache = permissionCache.value
    return cache.length === 1 && cache.types[0] === UserAccountType.Broker
  })
  const onlyCustomerView = computed(() => {
    const cache = permissionCache.value
    return cache.length === 1 && cache.types[0] === UserAccountType.Customer
  })
  const onlyCustomerServiceView = computed(() => {
    const cache = permissionCache.value
    return cache.length === 1 && cache.types[0] === UserAccountType.CustomerService
  })

  const onlyBuAccountType = computed(() => {
    const cache = permissionCache.value
    return cache.length === 1 && cache.types[0] === UserAccountType.Bu
  })

  // 隐藏佣金模块 - 优化版本
  const hiddenCommissionModule = computed(() => {
    const cache = permissionCache.value

    // 只有客户权限
    if (cache.length === 1 && cache.hasCustomer) {
      return true
    }

    // 客户和客服权限的组合
    if (cache.length === 2 && cache.hasCustomer && cache.hasCustomerService) {
      return true
    }

    return false
  })

  function setInfo(newUser: Partial<UserAuthState>) {
    // 用户信息会分多次回填：
    // 1. getRdxToken 返回基础用户信息
    // 2. getPermissionGroup 返回权限组
    // 因此这里使用 merge，而不是整对象替换。
    user.value = assign({}, user.value, newUser)
  }

  function resetInfo() {
    user.value = cloneDeep(defaultUser)
  }

  function setLoginStatus(status: LoginStatus) {
    loginStatus.value = status
  }

  async function loadUser() {
    const { emit } = store.evenStore
    setLoginStatus(1)

    try {
      const { data } = await getRdxToken<UserAuthState>()
      if (!data?.user_id) {
        resetInfo()
        clearToken()
        setLoginStatus(3)
        return
      }

      const nextUser: Partial<UserAuthState> = {
        ...data,
        permissionGroup: [],
      }

      try {
        const { data: permissionGroup } = await getPermissionGroup(data.user_id)
        nextUser.permissionGroup = permissionGroup ?? []
      }
      catch (error) {
        // permissionGroup 主要影响菜单/按钮级权限判断，不代表登录态本身失效。
        // 这里保留基础用户信息和 token，避免把临时接口异常放大成“被登出”。
        console.error('loadUser permissionGroup error', error)
      }

      // 登录链路至少需要“基础用户信息 + token”成功后才能进入已登录态。
      // permissionGroup 若临时失败，则以空数组兜底，避免网络抖动把用户误判成登录失效。
      setInfo(nextUser)
      setToken(data.access_token ?? '')
      const info = data.email ?? `${data.nick_name}(${data.user_id})`
      setUserId(info)
      trackPageView()
      setLoginStatus(2)
      emit(LOGIN_SUCCESS, { isCheck: true })
    }
    catch (error) {
      console.error('loadUser error', error)
      resetInfo()
      clearToken()
      setLoginStatus(3)
      throw error
    }
  }

  async function renewalRoles() {
    // try {
    const { content } = await loginTgc<{ redirectUrl: string }>()
    if (content) {
      const data = await get<string>(content?.redirectUrl)
      if (data && data.content) {
        saveCookie(COOKIE_JESSSION_ID_KEY, data.content, { path: '/' })
      }
    }
    // }
    // catch (error) {
    //   console.error('renewalRoles error :>>', error)
    // }
  }

  function logoutCallBack() {
    // const store = useAppStore();
    resetInfo()
    setLoginStatus(0)
    clearToken()
    clearCsToken()
    clearCustomerId()
    clearSimulated()
    // 同一个浏览器 tab 重新登录时，sessionStorage 不会自动清空。
    // 这里显式移除全局筛选与历史 Header 视图缓存，避免新登录账号继承上一个会话的视图和筛选上下文。
    SESSION_RESET_KEYS.forEach((key) => {
      removeSessionStorage(key)
    })
    // removeRouteListener();
    // removeModalListener();
    // store.clearServerMenu();
  }

  async function logout() {
    try {
      const data = await userLogout()
      logoutCallBack()
      const authRedirectUrl = resolveAuthRedirectUrl(data?.content)
      if (authRedirectUrl) {
        clearLoginCallbackUrl()
        window.location.replace(authRedirectUrl)
        return
      }
    }
    catch (error) {
      console.error('logout error', error)
      logoutCallBack()
    }

    clearLoginCallbackUrl()
    if (!isLocalDebugDomain()) {
      window.location.replace(getLoginEntryUrl())
    }
  }

  return {
    user,
    loginStatus,
    superAdmin,
    includeSuperView,
    includeBrokerView,
    includeCustomerServiceView,
    includeCustomerView,
    includeBuAccountType,
    hasBrokerUserViewType,
    hasBuUserViewType,
    hiddenCommissionModule,
    onlySuperView,
    onlyBrokerView,
    onlyCustomerView,
    onlyCustomerServiceView,
    onlyBuAccountType,
    setInfo,
    resetInfo,
    loadUser,
    renewalRoles,
    logoutCallBack,
    logout,
    setLoginStatus,
  }
}, {
  // https://prazdevs.github.io/pinia-plugin-persistedstate/zh/guide/config.html
  persist: {
    key: 'auth',
    paths: ['user'],
  },
})

export default useAuthStore
